import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { PaginatorState } from 'primeng/paginator';
import { MetaPagination } from 'src/app/core/models/meta.interface';
import { PaBulkAction } from 'src/app/shared/ui-kit/pa-bulk-actions-bar/models/pa-bulk-actions-bar.model';
import { PaTableComponent } from 'src/app/shared/ui-kit/pa-table/pa-table.component';
import { PaTableAction, PaTableColumn, PaTableColumnType, PaTableData } from 'src/app/shared/ui-kit/pa-table/models/pa-table.model';
import { FilterBarComponent } from 'src/app/shared/components/filter-bar/filter-bar.component';
import { FilterResult, FilterValues } from 'src/app/shared/models/filter.interface';

@Component({
  selector: 'app-bulk-actions-bar-view',
  standalone: true,
  imports: [CommonModule, PaTableComponent, FilterBarComponent],
  templateUrl: './bulk-actions-bar-view.component.html',
  styleUrls: ['./bulk-actions-bar-view.component.scss'],
})
export class BulkActionsBarViewComponent {
  defaultFilterData: FilterValues = {};

  // Пример без пагинации
  pagination: MetaPagination = {
    total_count: 3,
    page: 0,
    per_page: 30,
  };

  products: PaTableData[] = [
    {
      id: '1000',
      code: 'f230fh0g3',
      name: 'Bamboo Watch',
      category: 'Accessories',
      price: 65,
      quantity: 24,
      createdAt: new Date('2024-01-15'),
      isActive: true,
    },
    {
      id: '1001',
      code: 'f230fh0g4',
      name: 'Black Watch',
      category: 'Accessories',
      price: 72,
      quantity: 61,
      createdAt: new Date('2024-01-20'),
      isActive: true,
    },
    {
      id: '1002',
      code: 'f230fh0g5',
      name: 'Blue Band',
      category: 'Fitness',
      price: 79,
      quantity: 2,
      createdAt: new Date('2024-01-25'),
      isActive: false,
    },
  ];

  // Пример с пагинацией
  paginationPaginated: MetaPagination = {
    total_count: 100,
    page: 0,
    per_page: 10,
  };

  productsPaginated: PaTableData[] = Array.from({ length: 10 }).map(
    (_value, index) =>
      ({
        id: `p-${index + 1}`,
        code: `code-${index + 1}`,
        name: `Товар ${index + 1}`,
        category: index % 2 === 0 ? 'Accessories' : 'Fitness',
        price: 50 + index * 5,
        quantity: 10 + index,
        createdAt: new Date('2024-02-01'),
        isActive: index % 2 === 0,
      } satisfies PaTableData),
  );

  columns: PaTableColumn[] = [
    { field: 'id', header: 'ID', width: '80px' },
    { field: 'code', header: 'Код', width: '120px' },
    { field: 'name', header: 'Название', width: '200px' },
    { field: 'category', header: 'Категория', width: '150px' },
    { field: 'price', header: 'Цена', width: '100px', type: PaTableColumnType.Number },
    { field: 'quantity', header: 'Количество', width: '120px', type: PaTableColumnType.Number },
    { field: 'createdAt', header: 'Дата создания', width: '160px', type: PaTableColumnType.Date },
  ];

  tableActions: PaTableAction[] = [
    {
      id: 'edit',
      label: 'Редактировать',
      icon: 'pi pi-pencil',
      command: (item: PaTableData) => {
        console.log('Редактирование элемента:', item);
      },
    },
    {
      id: 'delete',
      label: 'Удалить',
      icon: 'pi pi-trash',
      command: (item: PaTableData) => {
        console.log('Удаление элемента:', item);
      },
    },
  ];

  selectedItems: PaTableData[] = [];
  selectedItemsPaginated: PaTableData[] = [];

  bulkActions: PaBulkAction[] = [
    /*{
      id: 'edit',
      label: 'Редактировать',
      icon: 'pi pi-pencil',
      command: () => {
        console.log('Массовое действие: редактировать', this.selectedItems);
      },
    },*/
    {
      id: 'delete',
      label: 'Удалить',
      icon: 'pi pi-trash',
      command: () => {
        console.log('Массовое действие: удалить', this.selectedItems);
      },
    },
  ];

  // Моковые данные для панели фильтров как в tasks-list
  filterData: FilterResult = {
    filters: {},
    count: 0,
  };

  hasUrlFilters = false;
  canEditTasks = true;

  handleSelectionChange(selectedItems: PaTableData[]): void {
    this.selectedItems = selectedItems;
  }

  handleSelectionChangePaginated(selectedItems: PaTableData[]): void {
    this.selectedItemsPaginated = selectedItems;
  }

  handleBulkClose(): void {
    this.selectedItems = [];
  }

  handleToggleSelectAll(shouldSelect: boolean): void {
    console.log('UI-Kit: toggleSelectAll (без пагинации)', shouldSelect);
  }

  handleBulkClosePaginated(): void {
    this.selectedItemsPaginated = [];
  }

  handleToggleSelectAllPaginated(shouldSelect: boolean): void {
    console.log('UI-Kit: toggleSelectAll (с пагинацией)', shouldSelect);
  }

  handleBulkAction(action: PaBulkAction): void {
    console.log('Массовое действие выполнено:', action.id || action.label);
  }

  handleBulkActionPaginated(action: PaBulkAction): void {
    console.log(
      'Массовое действие (с пагинацией) выполнено:',
      action.id || action.label,
    );
  }

  handlePageChangePaginated(event: PaginatorState): void {
    const page = event.page ?? 0;
    const perPage = event.rows ?? this.paginationPaginated.per_page;
    const offset = page * perPage;

    this.paginationPaginated = { ...this.paginationPaginated, page };
    this.productsPaginated = Array.from({ length: perPage }).map(
      (_value, index) => ({
        id: `p-${offset + index + 1}`,
        code: `code-${offset + index + 1}`,
        name: `Товар ${offset + index + 1}`,
        category: (offset + index) % 2 === 0 ? 'Accessories' : 'Fitness',
        price: 50 + (offset + index) * 5,
        quantity: 10 + (offset + index),
        createdAt: new Date('2024-02-01'),
        isActive: (offset + index) % 2 === 0,
      } satisfies PaTableData),
    );
  }

  // Моковые обработчики для app-filter-bar
  openEditPanel(): void {
    console.log('UI-Kit: openEditPanel() вызван');
  }

  async openFilterPanel(): Promise<void> {
    console.log('UI-Kit: openFilterPanel() вызван');
  }
}

